import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import useActionGuard from "../../hooks/useActionGuard";
import { useIsMobile } from "../../hooks/useMediaQuery";
import SelectionToolbar from "./SelectionToolbar";
import "./ContextMenu.css";

const ContextMenu = ({
  isVisible,
  position,
  selectionRect,
  onClose,
  menuItems = [],
  selectionToolbar = null,
}) => {
  const { t } = useTranslation();
  const menuRef = useRef(null);
  const isMobile = useIsMobile();

  const hasWindow = typeof window !== "undefined";
  const hasNavigator = typeof navigator !== "undefined";
  const currentWidth = hasWindow ? window.innerWidth : 0;
  const isTouchDevice =
    hasWindow && hasNavigator
      ? "ontouchstart" in window || navigator.maxTouchPoints > 0
      : false;
  const isTabletLikeTouchDevice =
    isTouchDevice && currentWidth > 768 && currentWidth <= 1366;

  // Detect iPad/Safari specifically for safe area handling
  const isIpad =
    hasNavigator &&
    /iPad|Macintosh/.test(navigator.userAgent) &&
    navigator.maxTouchPoints > 0;

  const isCompactMenu = isMobile || isTabletLikeTouchDevice;
  // Same toolbar UI for desktop + mobile; mobile docks to the bottom over the bar.
  const useSelectionToolbar = Boolean(selectionToolbar);
  const isBottomDocked = useSelectionToolbar && isMobile;
  const hasContent = useSelectionToolbar
    ? true
    : Array.isArray(menuItems) && menuItems.length > 0;

  useEffect(() => {
    if (!isVisible) return undefined;

    let removeListeners = null;
    let cancelled = false;

    // Defer outside-click binding so the same click that opened the menu
    // (e.g. selecting an existing highlight) does not immediately close it.
    const bindTimer = window.setTimeout(() => {
      if (cancelled) return;

      const handleClickOutside = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
          onClose();
          return;
        }
        // LanguageDropdown panel portals to body — keep the selection menu open
        if (target.closest(".language-dropdown-panel")) {
          return;
        }
        if (menuRef.current && !menuRef.current.contains(target)) {
          onClose();
        }
      };

      const handleEscape = (event) => {
        if (event.key === "Escape") {
          onClose();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
      document.addEventListener("keydown", handleEscape);

      removeListeners = () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("touchstart", handleClickOutside);
        document.removeEventListener("keydown", handleEscape);
      };
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(bindTimer);
      if (removeListeners) removeListeners();
    };
  }, [isVisible, onClose]);

  const [dimensions, setDimensions] = React.useState({
    width: useSelectionToolbar ? 280 : 220,
    height: useSelectionToolbar ? 96 : 200,
  });
  const { run: runMenuAction, isRunning: isMenuActionRunning } = useActionGuard(
    async (action) => {
      if (typeof action === "function") {
        await Promise.resolve(action());
        return;
      }
      if (!action?.onClick) return;
      await Promise.resolve(action.onClick());
    },
  );

  // Measure full dimensions when menu becomes visible or content changes
  React.useLayoutEffect(() => {
    if (isVisible && menuRef.current && !isBottomDocked) {
      // Clear max height temporarily to get intrinsic height
      const originalMaxHeight = menuRef.current.style.maxHeight;
      menuRef.current.style.maxHeight = "none";
      const rect = menuRef.current.getBoundingClientRect();
      menuRef.current.style.maxHeight = originalMaxHeight;
      setDimensions({ width: rect.width, height: rect.height });
    }
  }, [
    isVisible,
    menuItems.length,
    useSelectionToolbar,
    isBottomDocked,
    selectionToolbar?.activeColor,
    selectionToolbar?.canDelete,
  ]);

  // Calculate adjusted position and max height (floating placement only)
  const { coords, finalMaxHeight } = React.useMemo(() => {
    if (!isVisible || isBottomDocked) {
      return { coords: position, finalMaxHeight: null };
    }

    const viewport = window.visualViewport;
    const viewportWidth = viewport?.width ?? window.innerWidth;
    const viewportHeight = viewport?.height ?? window.innerHeight;
    const viewportTopInset = viewport?.offsetTop ?? 0;
    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

    // Reserve space used by fixed mobile chrome within the app (top/bottom bars).
    const getVisibleElementHeight = (selector, isBottom = false) => {
      if (typeof document === "undefined") return 0;
      const el = document.querySelector(selector);
      if (!el) return 0;

      const style = window.getComputedStyle(el);
      if (
        style.display === "none" ||
        style.visibility === "hidden" ||
        Number(style.opacity) === 0
      ) {
        return 0;
      }

      const rect = el.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return 0;

      const viewportBottom = viewportHeight;
      if (isBottom) {
        if (rect.top >= viewportBottom) return 0;
        return Math.max(0, viewportBottom - Math.max(rect.top, 0));
      }

      if (rect.bottom <= 0) return 0;
      return Math.max(
        0,
        Math.min(rect.bottom, viewportBottom) - Math.max(rect.top, 0),
      );
    };

    const getVisibleElementWidth = (selector) => {
      if (typeof document === "undefined") return 0;
      const el = document.querySelector(selector);
      if (!el) return 0;

      const style = window.getComputedStyle(el);
      if (
        style.display === "none" ||
        style.visibility === "hidden" ||
        Number(style.opacity) === 0
      ) {
        return 0;
      }

      const rect = el.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return 0;
      return rect.width;
    };

    const reservedTop = isCompactMenu
      ? getVisibleElementHeight(".mobile-top-bar")
      : getVisibleElementHeight(".toolbar");
    const reservedBottom = isCompactMenu
      ? getVisibleElementHeight(".mobile-bottom-bar", true)
      : 0;
    const reservedRight = isCompactMenu
      ? 0
      : getVisibleElementWidth(".right-sidebar");

    const margin = isCompactMenu ? 8 : 10;
    const minScrollableHeight = isCompactMenu ? 120 : 140;
    const { width, height } = dimensions;

    const belowOffset = isCompactMenu ? 8 : 10;
    // iPad needs more space for Safari top bar and safe areas
    const aboveOffset = isIpad ? 60 : isTabletLikeTouchDevice ? 28 : 20;
    const sideOffset = isCompactMenu ? 10 : 12;

    // Detect multi-page selections early: if selection height is very large, it spans multiple pages
    const isMultiPageSelection =
      selectionRect && selectionRect.height > viewportHeight * 0.5;

    // iPad needs additional minimum top offset for Safari's top bar and notch/safe area
    const iPadTopBuffer = isIpad ? (isMultiPageSelection ? 40 : 30) : 0;
    const minTop = Math.max(
      viewportTopInset + margin + reservedTop,
      iPadTopBuffer + margin,
    );
    const maxBottom = Math.max(
      minTop + 80,
      viewportHeight - margin - reservedBottom,
    );
    const maxRight = Math.max(
      margin + width,
      viewportWidth - margin - reservedRight,
    );

    const belowY = position.y + belowOffset;
    const availableBelow = maxBottom - belowY;
    const availableAbove = position.y - minTop - aboveOffset;
    const hasSelectionRect = Boolean(selectionRect);
    const selectionCenterY = selectionRect
      ? selectionRect.top + selectionRect.height / 2
      : position.y;
    const availableRight = selectionRect
      ? maxRight - (selectionRect.right + sideOffset)
      : 0;
    const availableLeft = selectionRect
      ? selectionRect.left - margin - sideOffset
      : 0;

    let finalY = belowY;
    let currentMaxHeight = null;
    let finalX = position.x;
    const canPlaceBelow = availableBelow >= height;
    const canPlaceAbove = availableAbove >= height;
    const canPlaceRight = hasSelectionRect && availableRight >= width;
    const canPlaceLeft = hasSelectionRect && availableLeft >= width;

    const estimatedMenuHeightForPlacement = useSelectionToolbar ? 130 : 180;

    // For iPad, only prefer above when there is truly not enough room below.
    // Never use above positioning for multi-page selections on iPad.
    const shouldPreferAbove = isIpad
      ? selectionRect &&
        !isMultiPageSelection &&
        selectionRect.bottom > viewportHeight * 0.65 &&
        availableBelow < estimatedMenuHeightForPlacement
      : isTabletLikeTouchDevice &&
        selectionRect &&
        selectionRect.bottom > viewportHeight * 0.55;

    const aboveAnchorY = selectionRect ? selectionRect.top : position.y;

    if (shouldPreferAbove && availableAbove > 0) {
      if (availableAbove >= height) {
        finalY = aboveAnchorY - height - aboveOffset;
      } else {
        currentMaxHeight = Math.max(minScrollableHeight, availableAbove);
        const renderHeight = Math.min(height, currentMaxHeight);
        finalY = aboveAnchorY - renderHeight - aboveOffset;
      }
    } else if (canPlaceBelow) {
      finalY = belowY;
    } else if (canPlaceAbove) {
      finalY = aboveAnchorY - height - aboveOffset;
    } else if (canPlaceRight) {
      finalX = selectionRect.right + sideOffset;
      finalY = clamp(selectionCenterY - height / 2, minTop, maxBottom - height);
      currentMaxHeight = Math.max(minScrollableHeight, maxBottom - minTop);
    } else if (canPlaceLeft) {
      finalX = selectionRect.left - width - sideOffset;
      finalY = clamp(selectionCenterY - height / 2, minTop, maxBottom - height);
      currentMaxHeight = Math.max(minScrollableHeight, maxBottom - minTop);
    } else if (availableBelow >= availableAbove) {
      finalY = belowY;
      currentMaxHeight = Math.max(minScrollableHeight, availableBelow);
    } else {
      currentMaxHeight = Math.max(minScrollableHeight, availableAbove);
      const renderHeight = Math.min(height, currentMaxHeight);
      finalY = aboveAnchorY - renderHeight - aboveOffset;
    }

    if (finalY < minTop) {
      finalY = minTop;
    }

    if (isIpad) {
      const ipadMinimumY = isMultiPageSelection
        ? iPadTopBuffer + 16
        : iPadTopBuffer + 8;
      if (finalY < ipadMinimumY) {
        finalY = ipadMinimumY;
      }
    }

    const bottomOverflow = finalY + height - maxBottom;
    if (bottomOverflow > 0) {
      const fitHeight = maxBottom - finalY;
      if (fitHeight > 0) {
        currentMaxHeight = currentMaxHeight
          ? Math.min(currentMaxHeight, fitHeight)
          : fitHeight;
      } else {
        finalY = minTop;
        currentMaxHeight = maxBottom - minTop;
      }
    }

    const maxViewportHeight = maxBottom - minTop;
    if (currentMaxHeight) {
      currentMaxHeight = Math.max(
        80,
        Math.min(currentMaxHeight, maxViewportHeight),
      );
    }

    if (isCompactMenu) {
      finalX = position.x - width / 2;
    }

    if (finalX + width > maxRight) {
      finalX = maxRight - width;
    }
    if (finalX < margin) {
      finalX = margin;
    }

    return {
      coords: { x: finalX, y: finalY },
      finalMaxHeight: currentMaxHeight,
    };
  }, [
    position,
    selectionRect,
    isVisible,
    dimensions,
    isCompactMenu,
    isTabletLikeTouchDevice,
    isIpad,
    useSelectionToolbar,
    isBottomDocked,
  ]);

  if (!isVisible || !hasContent) {
    return null;
  }

  const menuStyle = isBottomDocked
    ? {
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        top: "auto",
        zIndex: 1100,
      }
    : {
        position: "fixed",
        left: coords.x,
        top: coords.y,
        maxHeight: finalMaxHeight ? `${finalMaxHeight}px` : undefined,
        zIndex: 1000,
      };

  const menuNode = (
    <div
      ref={menuRef}
      className={`context-menu${isCompactMenu ? " is-compact" : ""}${
        useSelectionToolbar ? " is-selection-toolbar" : ""
      }${isBottomDocked ? " is-bottom-docked" : ""}`}
      style={menuStyle}
      role="toolbar"
      aria-label={t("contextMenu.selectionToolbar", "Selection menu")}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      {useSelectionToolbar ? (
        <SelectionToolbar
          toolbar={selectionToolbar}
          isActionRunning={isMenuActionRunning}
          onAction={runMenuAction}
          onClose={onClose}
        />
      ) : (
        <div className="context-menu-items">
          {menuItems.map((item, index) => (
            <button
              key={index}
              className={`context-menu-item${
                item.disabled ? " is-disabled" : ""
              }`}
              onClick={async (e) => {
                e.stopPropagation();
                await runMenuAction(item);
                onClose();
              }}
              disabled={item.disabled || isMenuActionRunning}
              type="button"
            >
              <span className="context-menu-icon">{item.icon}</span>
              <span className="context-menu-label">{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Bottom-docked mobile menu portals to body so it reliably overlaps MobileBottomBar
  // even when the viewer tree has overflow/transform containing blocks.
  if (isBottomDocked && typeof document !== "undefined") {
    return createPortal(menuNode, document.body);
  }

  return menuNode;
};

export default ContextMenu;
